import { NavLink, useLocation } from "react-router-dom";
import { useUserGroup } from "../hooks/useUserGroup";
import { showsV2Studio } from "../lib/userGroups";
import { useSidebar } from "../context/SidebarContext";
import { MOBILE_BOTTOM_NAV_PATHS } from "./mobileNavConfig";

type Tab = {
  to: string;
  label: string;
  /** Nombre del glifo de Material Symbols Rounded. */
  icon: string;
  /** Override opcional del tamaño del icono (algunos glifos rinden más
   *  chicos/grandes ópticamente). Default: text-[26px]. */
  iconSize?: string;
};

// Dev testers: 4 tabs (Inicio = Interfaz V2, Modos, Historial, Mi marca).
const DEV_TABS: Tab[] = [
  // El glifo `home` rinde algo más chico, así que va un poco más grande.
  { to: "/interfaz-v2", label: "Inicio", icon: "home", iconSize: "text-[30px]" },
  { to: "/modos", label: "Modos", icon: "view_agenda" },
  { to: "/redes-sociales/imagenes", label: "Historial", icon: "history" },
  { to: "/mi-marca", label: "Mi marca", icon: "shopping_bag" },
];

// No dev testers: 3 tabs (Estudio como hasta ahora, Historial, Mi marca).
const BASE_TABS: Tab[] = [
  {
    to: "/redes-sociales/crear-anuncio/seleccionar-productos",
    label: "Estudio",
    icon: "image",
  },
  { to: "/redes-sociales/imagenes", label: "Historial", icon: "history" },
  { to: "/mi-marca", label: "Mi marca", icon: "shopping_bag" },
];

/**
 * Barra de navegación inferior para mobile. Solo se muestra en las
 * "pantallas-tab" (ver MOBILE_BOTTOM_NAV_PATHS). Reemplaza al menú hamburguesa
 * como navegación principal en mobile.
 *
 * Iconos (Material Symbols Rounded): inactivo = outline de líneas finas
 * (wght 300); activo = versión rellena (FILL 1) + color primario. Ver clases
 * `.bottomnav-icon` / `.bottomnav-icon--active` en index.css.
 */
export const MobileBottomNav = () => {
  const location = useLocation();
  const { isDevTester, grupo } = useUserGroup();
  const { mobileBackOverride, mobileHideBottomNav } = useSidebar();

  // Solo visible en las pantallas raíz de cada tab.
  if (!MOBILE_BOTTOM_NAV_PATHS.includes(location.pathname)) return null;

  // En lugar de desmontarla de golpe, la barra se desliza hacia abajo (con un
  // fundido sutil) cuando:
  //  · hay un sub-flujo con su propio back (preset sheet de Interfaz V2 —capa
  //    z-[35]—, o la vista detalle del historial), o
  //  · la pantalla entra en "modo tarea" con su propia barra de acción fija
  //    (ej. selección de productos con el CTA de generar).
  // Así, al abrir una imagen ampliada en el historial, la nav se va con una
  // animación suave en vez de parpadear.
  const hidden = mobileBackOverride !== null || mobileHideBottomNav;

  // Set de tabs según la experiencia de estudio: V2 (Inicio/Interfaz V2, Modos,
  // Historial, Mi marca) para dev testers y Grupo B; base (Estudio, Historial,
  // Mi marca) para Grupo A. Mientras no se conoce el grupo asumimos el set base;
  // el cache de localStorage hace que el set correcto aparezca al instante en
  // visitas posteriores.
  const tabs = showsV2Studio(isDevTester, grupo) ? DEV_TABS : BASE_TABS;

  return (
    <nav
      aria-hidden={hidden}
      className={`md:hidden select-none fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-black/[0.06] rounded-t-[22px] shadow-[0_-2px_16px_rgba(2,6,43,0.06)] transition-[transform,opacity] duration-300 ease-out ${
        hidden
          ? "translate-y-full opacity-0 pointer-events-none"
          : "translate-y-0 opacity-100"
      }`}
      // `WebkitTouchCallout`/`userSelect` en none: al mantener apretado en iOS
      // ya no aparece el menú de selección (Copy/Look Up/Translate) ni se
      // seleccionan los íconos/labels. El feedback de "pressed" lo da el
      // `active:opacity-60` de cada tab.
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      <div className="flex items-stretch h-16">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end
            className="flex-1 flex flex-col items-center justify-center gap-1 active:opacity-60"
          >
            {({ isActive }) => {
              const color = isActive
                ? "text-brand-primary"
                : "text-brand-black/45";
              return (
                <>
                  <span
                    className={`material-symbols-rounded leading-none transition-colors ${
                      tab.iconSize ?? "text-[26px]"
                    } ${
                      isActive ? "bottomnav-icon--active" : "bottomnav-icon"
                    } ${color}`}
                  >
                    {tab.icon}
                  </span>
                  <span
                    className={`text-[11px] font-medium leading-none transition-colors ${color}`}
                  >
                    {tab.label}
                  </span>
                </>
              );
            }}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};
